import { pageColumnClasses } from "@/components/layout/contentLayout.tsx";
import { PublicLogoNav } from "@/components/layout/PublicLogoNav.tsx";
import {
  MainContentWrapper,
  SiteHeaderContent,
  ViewWrapper,
} from "@/components/layout/ViewWrapper.tsx";
import { MessageList } from "@/components/messages/MessageList.tsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import type { ThreadSnapshotData } from "@/types/sharedSnapshot.ts";
import { userMessageTypes } from "@/types/messages.ts";
import type { GetMessageListResp, MessageThreadEvent } from "@/types/threadEvents.ts";
import type { GetThreadResp } from "@/types/thread.ts";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import { isAxiosError } from "axios";
import { ErrorBoundary } from "react-error-boundary";
import { useParams } from "wouter";

interface PublicSnapshotResp {
  title: string;
  description: string | null;
  createdAt: string;
  expiresAt: string | null;
  snapshot: ThreadSnapshotData;
}

// Snapshot wire shape uses plain strings for `message.type` / `message.status`
// and strips `author.urn`. The MessageList renderer is keyed on narrower types,
// so widen via a single assertion at this boundary after backfilling `urn`.
type RendererMessage = MessageThreadEvent["message"];

/**
 * Which messages in a legacy snapshot render their `content`.
 *
 * Capture now persists the real `shouldDisplayContent`, but snapshots taken
 * before it did are immutable blobs in GCS, so for those the flag is gone for
 * good and the renderer — which reads a missing flag as "hide" — dropped every
 * user turn from the share (BIG-7434). Agent answers survived only because they
 * render from a `userResponse` artifact instead, which is what made the loss
 * look selective rather than total.
 *
 * Nothing in a legacy blob distinguishes an agent breadcrumb from a bare-content
 * agent reply — same type, same shape, and the flag that told them apart is the
 * one that's missing. So this errs toward the user's own turns, which are always
 * worth reading, and leaves agent content that has no artifact hidden rather
 * than unfolding a wall of "Searching your inbox…" into a public page. New
 * snapshots don't reach this path at all.
 */
function shouldDisplayContentInLegacyShare(messageType: string): boolean {
  return userMessageTypes.safeParse(messageType).success;
}

function buildMessageListResp(snapshot: ThreadSnapshotData): GetMessageListResp {
  const items: MessageThreadEvent[] = snapshot.events.map((event) => ({
    ...event,
    message: {
      ...event.message,
      // `??`, not `||`: a captured `false` is the real answer and must win over
      // the legacy guess. It also keeps this deploy-order-safe — a frontend that
      // ships before the backend simply sees `undefined` and derives, exactly as
      // it does today.
      shouldDisplayContent:
        event.message.shouldDisplayContent ?? shouldDisplayContentInLegacyShare(event.message.type),
      // `urn` is stripped server-side on snapshot capture (snapshots are
      // public so the account-scoped URN is meaningless); the renderer type
      // still requires it. Nothing on the FE actually reads urn, so an empty
      // string is a placeholder, not real data. `author` itself stays
      // required in the non-snapshot codepath.
      author: event.message.author ? { ...event.message.author, urn: "" } : undefined,
    } as RendererMessage,
  }));
  return { items };
}

// Create a minimal mock thread for the MessageList component
function createMockThread(snapshot: ThreadSnapshotData): GetThreadResp {
  return {
    id: 0, // Use 0 to prevent any real thread actions
    status: "completed",
    isPaused: false,
    pinnedAt: null,
    title: snapshot.threadTitle,
    activeMessage: null,
    labels: [],
    sortDate: new Date(snapshot.createdAt),
    lastVisitedAt: null,
    unreadCount: 0,
    isSeen: true,
    activityState: "stopped",
    channel: "app",
  };
}

// Error fallback component
const MessageListErrorFallback = ({ error }: { error: unknown }) => {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center space-y-4 p-8">
        <h2 className="text-xl font-semibold text-red-600">Error Loading Messages</h2>
        <div className="text-sm text-muted-foreground max-w-lg">
          <pre className="bg-red-50 p-3 rounded text-left overflow-auto">
            {error instanceof Error ? error.message : "Unknown error"}
          </pre>
        </div>
      </div>
    </div>
  );
};

// Loading skeleton
const LoadingSkeleton = () => (
  <MainContentWrapper columns={8}>
    <div className="space-y-4 py-8">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-4 w-96" />
      <div className="space-y-4 mt-8">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    </div>
  </MainContentWrapper>
);

// Error display
const ErrorDisplay = ({ message }: { message: string }) => (
  <MainContentWrapper columns={7}>
    <div className="flex flex-col items-center justify-center min-h-[80vh] py-8">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="items-center">
          <CardTitle className="text-center text-xl font-bold text-destructive">
            Snapshot Not Available
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center text-muted-foreground">{message}</CardContent>
      </Card>
    </div>
  </MainContentWrapper>
);

export const SharedSnapshot = () => {
  const params = useParams<{ shareToken: string }>();
  const shareToken = params.shareToken;

  const {
    data: snapshotData,
    isPending,
    error,
  } = useQuery({
    queryKey: ["shared-snapshot", shareToken],
    queryFn: async ({ signal }) => {
      const response = await api.get<PublicSnapshotResp>(`/api/p/shared/${shareToken}`, { signal });
      return response.data;
    },
    enabled: !!shareToken,
    staleTime: Number.POSITIVE_INFINITY, // Snapshots are immutable
    retry: false, // Don't retry on 404
  });

  // Header with logo and snapshot info
  const headerContent = (
    <SiteHeaderContent NavComponent={PublicLogoNav}>
      {snapshotData && (
        <>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg @7xl:text-xl ml-4 font-semibold truncate">
              {snapshotData.title}
            </h1>
          </div>
          <div className="text-sm text-muted-foreground whitespace-nowrap">
            Shared on {new Date(snapshotData.createdAt).toLocaleDateString()}
            {snapshotData.expiresAt && (
              <span className="ml-2">
                (Expires {new Date(snapshotData.expiresAt).toLocaleDateString()})
              </span>
            )}
          </div>
        </>
      )}
    </SiteHeaderContent>
  );

  if (isPending) {
    return (
      <ViewWrapper headerContent={headerContent}>
        <LoadingSkeleton />
      </ViewWrapper>
    );
  }

  if (error || !snapshotData) {
    const errorMessage =
      isAxiosError(error) && error.response?.status === 404
        ? "This shared thread snapshot was not found or has expired."
        : "An error occurred while loading the snapshot.";

    return (
      <ViewWrapper headerContent={headerContent}>
        <ErrorDisplay message={errorMessage} />
      </ViewWrapper>
    );
  }

  const mockThread = createMockThread(snapshotData.snapshot);
  const messageListResp = buildMessageListResp(snapshotData.snapshot);

  return (
    <ViewWrapper headerContent={headerContent}>
      <ErrorBoundary
        FallbackComponent={MessageListErrorFallback}
        onError={(err, errorInfo) => {
          console.error("MessageList error:", err, errorInfo);
        }}
      >
        <MessageList
          thread={mockThread}
          debugData={{ messages: messageListResp }}
          focusType="desktop-only"
          contentWrapperClassName={pageColumnClasses["8"]}
          readOnly
        />
      </ErrorBoundary>
    </ViewWrapper>
  );
};
